import React, { useState, useEffect } from 'react';
import BaseModal from '../base/BaseModal';
import CustomSelect from '../base/CustomSelect';
import { Calendar, CheckCircle2, Loader2, Zap, Clock, AlertTriangle, RefreshCw, X, Save } from 'lucide-react';
import { REPORT_API } from '../../apis/report';
import { showToast } from '../../utils/toastNotification';
import DateTimePicker from '../../utils/datePicker';
import dayjs from 'dayjs';

export default function ReportApproveModal({
  isOpen,
  onClose,
  report,
  onSuccess
}) {
  const [currentMucDo, setCurrentMucDo] = useState('Thông thường');
  const isUrgent = currentMucDo.toLowerCase().includes('khẩn');
  const isOverduePending = report?.thoi_gian_tao && (dayjs().diff(dayjs(report.thoi_gian_tao), 'hour') >= 24);

  const [expectedDate, setExpectedDate] = useState(null);
  const [selectedOfficer, setSelectedOfficer] = useState('');
  const [leadershipNote, setLeadershipNote] = useState('');
  const [officers, setOfficers] = useState([]);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Trạng thái đổi mức độ
  const [isChangingMucDo, setIsChangingMucDo] = useState(false);
  const [targetMucDo, setTargetMucDo] = useState('');
  const [reasonMucDo, setReasonMucDo] = useState('');
  const [isSubmittingMucDo, setIsSubmittingMucDo] = useState(false);

  useEffect(() => {
    if (isOpen && report) {
      const initialMucDo = report?.muc_do || report?.mucDo || 'Thông thường';
      setCurrentMucDo(initialMucDo);

      const urgent = initialMucDo.toLowerCase().includes('khẩn');
      if (urgent) {
        setExpectedDate(dayjs().add(24, 'hour'));
      } else {
        setExpectedDate(dayjs().add(3, 'day'));
      }

      setSelectedOfficer('');
      setLeadershipNote('');
      setIsChangingMucDo(false);
      setReasonMucDo('');
      setHasAttemptedSubmit(false);

      // Load assignable officers
      setLoadingOfficers(true);
      REPORT_API.getAssignableUsers(report.id)
        .then((res) => {
          setOfficers(Array.isArray(res) ? res : []);
        })
        .catch((err) => {
          console.error('Error loading officers:', err);
          setOfficers([]);
        })
        .finally(() => {
          setLoadingOfficers(false);
        });
    }
  }, [isOpen, report]);

  const handleToggleChangeMucDo = () => {
    if (!isChangingMucDo) {
      setTargetMucDo(isUrgent ? 'Thông thường' : 'Khẩn cấp');
      setReasonMucDo('');
    }
    setIsChangingMucDo(!isChangingMucDo);
  };

  const handleSaveMucDo = async () => {
    if (!reasonMucDo.trim()) {
      showToast.error('Vui lòng nhập lý do thay đổi mức độ!');
      return;
    }
    setIsSubmittingMucDo(true);
    try {
      await REPORT_API.updateMucDoReport(report.id, targetMucDo, reasonMucDo.trim());
      showToast.success(`Cập nhật mức độ thành "${targetMucDo}" thành công!`);

      // Cập nhật trạng thái mức độ tại giao diện
      setCurrentMucDo(targetMucDo);
      const newUrgent = targetMucDo.toLowerCase().includes('khẩn');
      if (newUrgent) {
        setExpectedDate(dayjs().add(24, 'hour'));
      } else {
        setExpectedDate(dayjs().add(3, 'day'));
      }

      setIsChangingMucDo(false);
      setReasonMucDo('');
      if (onSuccess) onSuccess();
    } catch (err) {
      showToast.error(err?.message || 'Có lỗi xảy ra khi cập nhật mức độ');
    } finally {
      setIsSubmittingMucDo(false);
    }
  };

  if (!isOpen || !report) return null;

  const handleApprove = async () => {
    setHasAttemptedSubmit(true);

    if (!selectedOfficer) {
      showToast.error('Vui lòng phân công chuyên viên thụ lý trước khi phê duyệt đơn');
      return;
    }
    if (!isUrgent && !expectedDate) {
      showToast.error('Vui lòng chọn ngày hoàn thành dự kiến');
      return;
    }

    setIsSubmitting(true);
    try {
      const completionDeadline = isUrgent
        ? dayjs().add(24, 'hour').toISOString()
        : dayjs(expectedDate).toISOString();

      // 1. Update status to 'Đang xử lý' with expected completion date
      await REPORT_API.updateStatusReport(report.id, {
        trangThai: 'Đang xử lý',
        ngayDuKienHoanThanh: completionDeadline,
        ghiChu: 'Lãnh đạo đã phê duyệt đơn phản ánh và phân công xử lý',
      });

      // 2. Assign to selected officer
      await REPORT_API.assignReport(report.id, selectedOfficer, leadershipNote.trim() || 'Phân công xử lý phản ánh');

      showToast.success('Phê duyệt và phân công xử lý phản ánh thành công!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast.error(err?.message || 'Có lỗi xảy ra khi phê duyệt phản ánh');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryName = report.linh_vuc_phan_anh?.ten || report.linh_vuc?.ten || 'Chưa phân loại';
  const neighborhood = report.khu_pho || 'Chưa rõ khu phố';
  const location = report.vi_tri || 'Không có thông tin vị trí';

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Phê duyệt đơn phản ánh"
      subtitle={`Mã phản ánh: ${report.ma_phan_anh || report.id}`}
      size="lg"
      className="max-w-xl"
    >
      <div className="space-y-4 py-1">
        {/* Cảnh báo trễ hạn tiếp nhận nếu đơn đã gửi lâu */}
        {isOverduePending && (
          <div className="flex items-center gap-2 text-xs text-rose-800 bg-rose-50 px-3.5 py-2.5 rounded-lg font-semibold border border-rose-200">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 animate-pulse" />
            <span>Đơn này đã gửi quá 24h và đang bị trễ hạn tiếp nhận. Vui lòng ưu tiên duyệt và phân công xử lý ngay!</span>
          </div>
        )}

        {/* Tóm tắt phản ánh Card */}
        <div className="bg-gray-50/80 border border-gray-200/80 rounded-lg p-3.5 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-bold text-gray-900 leading-snug m-0 line-clamp-2">
              {report.tieu_de || 'Phản ánh kiến nghị'}
            </h4>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md ${isUrgent
                  ? 'bg-red-100 text-red-700 border border-red-200 font-bold'
                  : 'bg-gray-200 text-gray-700 border border-gray-300'
                }`}>
                {currentMucDo}
              </span>
              <button
                type="button"
                onClick={handleToggleChangeMucDo}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center gap-1 cursor-pointer bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded border border-blue-200 transition-all"
                title="Đổi mức độ phản ánh"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Đổi mức độ</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 pt-1 border-t border-gray-200/60">
            <div>
              <span className="text-gray-500">Lĩnh vực: </span>
              <span className="font-semibold text-gray-800">{categoryName}</span>
            </div>
            <div>
              <span className="text-gray-500">Khu phố: </span>
              <span className="font-semibold text-gray-800">{neighborhood}</span>
            </div>
            <div className="col-span-2 truncate">
              <span className="text-gray-500">Vị trí: </span>
              <span className="font-medium text-gray-800">{location}</span>
            </div>
          </div>
        </div>

        {/* Khung đổi mức độ (khi click nút "Đổi mức độ") */}
        {isChangingMucDo && (
          <div className="p-3.5 bg-amber-50/90 border border-amber-300 rounded-lg space-y-3 shadow-xs animate-fadeIn">
            <div className="flex items-center justify-between border-b border-amber-200 pb-2">
              <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5 text-amber-600" />
                Thay đổi mức độ phản ánh
              </span>
              <button
                type="button"
                onClick={() => setIsChangingMucDo(false)}
                className="text-amber-700 hover:text-amber-900 p-0.5 rounded cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <label className={`flex items-center gap-2 p-2 rounded-md border transition-all ${
                isUrgent
                  ? 'opacity-40 bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : targetMucDo === 'Khẩn cấp'
                    ? 'border-red-500 bg-red-100/80 text-red-800 font-bold shadow-xs cursor-pointer'
                    : 'border-amber-200 bg-white text-gray-700 hover:bg-amber-50/50 cursor-pointer'
              }`}>
                <input
                  type="radio"
                  name="mucDoRadio"
                  value="Khẩn cấp"
                  disabled={isUrgent}
                  checked={targetMucDo === 'Khẩn cấp'}
                  onChange={(e) => setTargetMucDo(e.target.value)}
                  className="text-red-600 focus:ring-red-500"
                />
                <span className="text-xs">Khẩn cấp (24h)</span>
              </label>

              <label className={`flex items-center gap-2 p-2 rounded-md border transition-all ${
                !isUrgent
                  ? 'opacity-40 bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : targetMucDo === 'Thông thường'
                    ? 'border-blue-500 bg-blue-100/80 text-blue-800 font-bold shadow-xs cursor-pointer'
                    : 'border-amber-200 bg-white text-gray-700 hover:bg-amber-50/50 cursor-pointer'
              }`}>
                <input
                  type="radio"
                  name="mucDoRadio"
                  value="Thông thường"
                  disabled={!isUrgent}
                  checked={targetMucDo === 'Thông thường'}
                  onChange={(e) => setTargetMucDo(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs">Thông thường</span>
              </label>
            </div>

            <div>
              <label className="block text-xs font-semibold text-amber-900 mb-1">
                Lý do thay đổi mức độ <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={2}
                value={reasonMucDo}
                onChange={(e) => setReasonMucDo(e.target.value)}
                placeholder="Nhập lý do điều chỉnh mức độ phản ánh (ví dụ: chuyển thành khẩn cấp do tính chất nghiêm trọng...)"
                className="w-full px-3 py-2 text-xs bg-white border border-amber-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-gray-800 placeholder-gray-400"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setIsChangingMucDo(false)}
                disabled={isSubmittingMucDo}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSaveMucDo}
                disabled={isSubmittingMucDo}
                className="px-3.5 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-md shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {isSubmittingMucDo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span>Lưu thay đổi</span>
              </button>
            </div>
          </div>
        )}

        {/* Box thời gian xử lý: Đơn khẩn cấp (cố định 24h) vs Đơn thông thường (chọn ngày) */}
        {isUrgent ? (
          <div className="bg-red-50/70 border border-red-200 rounded-lg p-3.5 space-y-1.5">
            <div className="flex items-center gap-2 text-red-700 font-bold text-xs md:text-sm">
              <Zap className="w-4 h-4 text-red-600 shrink-0" />
              <span>Đơn khẩn cấp: Thời hạn xử lý quy định 24 giờ</span>
            </div>
            <p className="text-xs text-red-800 leading-relaxed m-0">
              Đơn phản ánh thuộc mức độ <strong>Khẩn cấp</strong>. Hệ thống tự động thiết lập thời hạn hoàn thành trong vòng <strong>24 giờ</strong> kể từ thời điểm phê duyệt.
            </p>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-900 pt-1">
              <Clock className="w-3.5 h-3.5 text-red-600" />
              <span>Hạn chót dự kiến: {dayjs().add(24, 'hour').format('DD/MM/YYYY HH:mm')}</span>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50/50 border border-blue-200 rounded-lg p-3.5 space-y-2">
            <div className="flex items-center gap-2 text-blue-700 font-bold text-xs md:text-sm">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span>Chọn thời gian xử lý dự kiến</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Ngày hoàn thành dự kiến <span className="text-red-500">*</span>
              </label>
              <DateTimePicker
                value={expectedDate ? (dayjs.isDayjs(expectedDate) ? expectedDate : dayjs(expectedDate)) : null}
                onChange={(dateObj) => setExpectedDate(dateObj)}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                showTime={{ format: 'HH:mm', minuteStep: 5, use12Hours: false }}
                format="DD/MM/YYYY HH:mm"
                placeholder="Chọn ngày và giờ hoàn thành dự kiến"
                style={{
                  width: '100%',
                  height: '38px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem'
                }}
              />
            </div>
          </div>
        )}

        {/* Phân công chuyên viên thụ lý */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Phân công chuyên viên thụ lý <span className="text-red-500">*</span>
          </label>
          <CustomSelect
            value={selectedOfficer}
            onChange={(val) => setSelectedOfficer(typeof val === 'object' ? val?.target?.value : val)}
            disabled={loadingOfficers}
            hasError={hasAttemptedSubmit && !selectedOfficer}
            placeholder="-- Chọn chuyên viên phân công phụ trách --"
            options={officers.map((officer) => ({
              value: officer.id,
              label: `${officer.ho_va_ten || officer.ten_dang_nhap} ${officer.so_dien_thoai ? `(${officer.so_dien_thoai})` : ''} - ${officer.vai_tro || 'Chuyên viên'}`
            }))}
          />
          {hasAttemptedSubmit && !selectedOfficer && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-medium">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span>Bắt buộc phải chọn chuyên viên thụ lý trước khi phê duyệt đơn</span>
            </p>
          )}
          {loadingOfficers && (
            <p className="text-[11px] text-gray-500 mt-1">Đang tải danh sách cán bộ phụ trách...</p>
          )}
        </div>

        {/* Ghi chú phê duyệt */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Ghi chú phê duyệt (Không bắt buộc)
          </label>
          <input
            type="text"
            value={leadershipNote}
            onChange={(e) => setLeadershipNote(e.target.value)}
            placeholder="Nhập ghi chú hoặc chỉ đạo cho cán bộ thụ lý..."
            className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md text-xs md:text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-all cursor-pointer"
          >
            Hủy bỏ
          </button>

          <button
            type="button"
            onClick={handleApprove}
            disabled={isSubmitting}
            className="px-5 py-2 rounded-md text-xs md:text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            <span>Phê duyệt đơn</span>
          </button>
        </div>
      </div>
    </BaseModal>
  );
}

